import { KpiService } from '@/services';
import tokens from '@/styles/tokens';
import { IKpiCompanyCode, IKpiSnapshot } from '@/types/response';
import {
  Alert,
  Tooltip as AntTooltip,
  Button,
  Card,
  ConfigProvider,
  Empty,
  Segmented,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from 'react-query';
import { DemographicsCard } from './DemographicsCard';
import { FamilyCohortTable } from './FamilyCohortTable';
import { ISeries, KpiChart, SERIES_COLORS, formatNumber } from './KpiChart';
import { Aggregation, Granularity, IMetric, findMissingDates, toBuckets } from './kpiSeries';

const { Title, Text } = Typography;

/**
 * カードと表はページ背景（bgContainer）と同じ色だとブロックの境目が見えないので、
 * この画面だけ白にする（全体のテーマは入力欄なども同じ色を使うので変えない）
 */
export const SURFACE_THEME = {
  components: {
    Card: { colorBgContainer: tokens.colors.bgBase },
    Table: { colorBgContainer: tokens.colors.bgBase },
  },
};

interface IProps {
  title?: string;
}

type Range = '30' | '90' | 'all';

/** 全期間の下限。kpi-collector の履歴（2026-04-24〜）より前ならよい */
const ALL_FROM = '2026-01-01';

const RANGE_OPTIONS = [
  { label: '直近30日', value: '30' },
  { label: '直近90日', value: '90' },
  { label: '全期間', value: 'all' },
];

const GRANULARITY_OPTIONS = [
  { label: '日次', value: 'day' },
  { label: '週次', value: 'week' },
  { label: '月次', value: 'month' },
];

/**
 * 複数人家族率（複数人家族 ÷ 総家族）。どちらかのキーが無い行（取り込み行）は undefined で
 * 「値なし」、総家族が0なら null（分母0）にする（kpiSeries の last の扱いに合わせる）
 */
const multiMemberRatePct = (r: IKpiSnapshot): number | null | undefined => {
  const { multiMemberFamilies: multi, totalFamilies: total } = r.daily;
  if (typeof multi !== 'number' || typeof total !== 'number') return undefined;
  if (total === 0) return null;

  return Math.round((multi / total) * 1000) / 10;
};

/**
 * 家族トークで利用者が送った数（コメント＋スタンプ＋その他。リアクションは送信ではないので含めない）。
 * 内訳が無い行（2026-10 より前）は undefined で「値なし」にする
 */
const familyTalkSent = (r: IKpiSnapshot): number | undefined => {
  const { familyTalkComments: c, familyTalkStamps: s, familyTalkOthers: o } = r.daily;
  if (typeof c !== 'number' || typeof s !== 'number' || typeof o !== 'number') return undefined;

  return c + s + o;
};

/** グラフに出す指標。agg の意味は kpiSeries.ts の Aggregation を参照 */
const M = {
  dau: { label: 'DAU', get: (r) => r.snapshot.dau, agg: 'last' },
  wau: { label: 'WAU', get: (r) => r.snapshot.wau, agg: 'last' },
  mau: { label: 'MAU', get: (r) => r.snapshot.mau, agg: 'last' },
  dag: { label: 'DAG', get: (r) => r.snapshot.dag, agg: 'last' },
  wag: { label: 'WAG', get: (r) => r.snapshot.wag, agg: 'last' },
  mag: { label: 'MAG', get: (r) => r.snapshot.mag, agg: 'last' },
  newUsers: { label: '新規ユーザー', get: (r) => r.daily.newUsers, agg: 'sum' },
  newFamilies: { label: '新規家族', get: (r) => r.daily.newFamilies, agg: 'sum' },
  totalFamilies: { label: '総家族数', get: (r) => r.daily.totalFamilies, agg: 'last' },
  multiMemberFamilies: {
    label: '複数人家族数',
    get: (r) => r.daily.multiMemberFamilies,
    agg: 'last',
  },
  multiMemberFamiliesNet: {
    label: '複数人家族の純増',
    get: (r) => r.daily.multiMemberFamilies,
    agg: 'delta',
  },
  secondMemberFamilies: {
    label: '2人目が加わった家族',
    get: (r) => r.daily.secondMemberFamilies,
    agg: 'sum',
  },
  multiMemberRatePct: { label: '複数人家族率', get: multiMemberRatePct, agg: 'last' },
  activeMultiMemberFamilies: {
    label: 'うちアクティブ',
    get: (r) => r.snapshot.activeMultiMemberFamilies,
    agg: 'last',
  },
  inviteLinksIssued: { label: '招待リンク発行', get: (r) => r.daily.inviteLinksIssued, agg: 'sum' },
  inviteLinksUsed: { label: '招待リンクから参加', get: (r) => r.daily.inviteLinksUsed, agg: 'sum' },
  inviteIssuingFamilies: {
    label: '招待した家族',
    get: (r) => r.daily.inviteIssuingFamilies,
    agg: 'sum',
  },
  inviteIssuingUsers: { label: '招待した人', get: (r) => r.daily.inviteIssuingUsers, agg: 'sum' },
  posts: { label: '投稿', get: (r) => r.daily.posts, agg: 'sum' },
  messages: { label: 'トーク合計', get: (r) => r.daily.messages, agg: 'sum' },
  familyTalkComments: { label: 'コメント', get: (r) => r.daily.familyTalkComments, agg: 'sum' },
  familyTalkStamps: { label: 'スタンプ', get: (r) => r.daily.familyTalkStamps, agg: 'sum' },
  familyTalkReactions: {
    label: 'リアクション',
    get: (r) => r.daily.familyTalkReactions,
    agg: 'sum',
  },
  familyTalkOthers: {
    label: 'その他',
    get: (r) => r.daily.familyTalkOthers,
    agg: 'sum',
  },
  familyTalkSent: { label: '家族トーク', get: familyTalkSent, agg: 'sum' },
  fukumaruTalkMessages: {
    label: 'フクマルとのチャット',
    get: (r) => r.daily.fukumaruTalkMessages,
    agg: 'sum',
  },
  comments: { label: 'コメント', get: (r) => r.daily.comments, agg: 'sum' },
  favorites: { label: 'いいね', get: (r) => r.daily.favorites, agg: 'sum' },
  activeFamilies: { label: 'アクティブ家族', get: (r) => r.daily.activeFamilies, agg: 'avg' },
  stepActiveUsers: { label: '歩数アクティブ UU', get: (r) => r.daily.stepActiveUsers, agg: 'avg' },
  cogevoUu: { label: 'CogEvo', get: (r) => r.daily.cogevoUu, agg: 'avg' },
  nouknowUu: { label: 'のうKNOW', get: (r) => r.daily.nouknowUu, agg: 'avg' },
  newPayingFamilies: { label: '新規課金家族', get: (r) => r.daily.newPayingFamilies, agg: 'sum' },
  newPayingRevenue: { label: '新規課金額', get: (r) => r.daily.newPayingRevenue, agg: 'sum' },
  payingRatePct: { label: '課金率', get: (r) => r.derived.payingRatePct, agg: 'last' },
  retention7dPct: { label: '7日継続率', get: (r) => r.derived.retention7dPct, agg: 'last' },
  pushReachablePct: {
    label: 'プッシュ到達可能率',
    get: (r) => r.derived.pushReachablePct,
    agg: 'last',
  },
  leavedMembers: { label: '離脱メンバー', get: (r) => r.daily.leavedMembers, agg: 'sum' },
  inquiries: { label: '問い合わせ', get: (r) => r.daily.inquiries, agg: 'sum' },
} satisfies Record<string, Omit<IMetric, 'id'>>;

type MetricId = keyof typeof M;

const METRICS: IMetric[] = (Object.keys(M) as MetricId[]).map((id) => ({ id, ...M[id] }));

const series = (...ids: MetricId[]): ISeries[] => ids.map((id) => ({ id, label: M[id].label }));

/** 粒度ごとの注記（件数は合計・UU は日平均・時点の値は期間の最終日・増減は最終日どうしの差） */
const aggNote = (g: Granularity, agg: Aggregation): string | undefined => {
  if (agg === 'delta') {
    return g === 'day' ? '前日との差' : '期間の最終日と、前の期間の最終日との差';
  }
  if (g === 'day') return undefined;
  if (agg === 'sum') return '期間の合計（両端の週・月は途中までの日数分）';
  if (agg === 'avg') return '期間の日平均';

  return '期間の最終日の値';
};

/**
 * 今あるべき最新の対象日。集計は毎朝 7:00 JST に前日分を作るので、
 * 余裕を見て 8:00 JST を過ぎたら前日、それまでは前々日。
 */
const expectedLatestDate = (): string => {
  const now = moment().utcOffset(9 * 60);

  return now
    .clone()
    .subtract(now.hour() >= 8 ? 1 : 2, 'days')
    .format('YYYY-MM-DD');
};

const joinNotes = (...notes: (string | undefined)[]) => notes.filter(Boolean).join('。') || undefined;

/** 取得失敗を「0件」に見せない（henka 画面と同じ方針） */
const LoadError: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <Alert
    type='error'
    showIcon
    message='取得できませんでした'
    description={
      <Space direction='vertical' size={4}>
        <Text>数字が出ていないのは「0」ではなく不明です。</Text>
        <Button size='small' onClick={onRetry}>
          再読み込み
        </Button>
      </Space>
    }
  />
);

// ---- 主要指標カード -------------------------------------------------------

interface ICardDef {
  label: string;
  get: (r: IKpiSnapshot) => number | null | undefined;
  /** 割合は差をポイントで出す */
  pct?: boolean;
  hint: string;
}

const CARDS: ICardDef[] = [
  {
    label: '複数人家族',
    get: (r) => r.daily.multiMemberFamilies,
    hint: 'メンバー2人以上の家族（対象日の終わり時点）',
  },
  { label: '複数人家族率', get: multiMemberRatePct, pct: true, hint: '複数人家族 ÷ 総家族' },
  { label: 'DAU', get: (r) => r.snapshot.dau, hint: '直近24時間にアプリを開いた人' },
  { label: 'MAU', get: (r) => r.snapshot.mau, hint: '直近30日にアプリを開いた人' },
  { label: 'Stickiness', get: (r) => r.derived.stickinessPct, pct: true, hint: 'DAU ÷ MAU' },
  { label: 'MAG', get: (r) => r.snapshot.mag, hint: '直近30日にトークか投稿があった家族' },
  { label: '課金率', get: (r) => r.derived.payingRatePct, pct: true, hint: 'MAG のうち課金中の家族' },
  { label: '総ユーザー数', get: (r) => r.daily.totalUsers, hint: '対象日の終わり時点' },
];

const diffText = (
  current: number | null | undefined,
  base: number | null | undefined,
  pct?: boolean,
): string => {
  if (typeof current !== 'number' || typeof base !== 'number') return '—';
  const d = Math.round((current - base) * 10) / 10;
  const sign = d > 0 ? '▲' : d < 0 ? '▼' : '±';

  return `${sign}${Math.abs(d).toLocaleString('ja-JP')}${pct ? 'pt' : ''}`;
};

const KpiCards: React.FC<{
  latest: IKpiSnapshot;
  previousDay: IKpiSnapshot | null;
  weekAgo: IKpiSnapshot | null;
}> = ({ latest, previousDay, weekAgo }) => (
  <div className='grid grid-cols-2 md:grid-cols-4 gap-md'>
    {CARDS.map((c) => {
      const value = c.get(latest);

      return (
        <Card key={c.label} size='small'>
          <div className='text-fg-muted text-[12px]'>{c.label}</div>
          <div className='text-fg text-[22px] font-semibold leading-tight my-xs'>
            {formatNumber(value, c.pct ? '%' : '')}
          </div>
          <div className='text-fg-muted text-[12px] leading-relaxed'>
            <div>前日比 {diffText(value, previousDay && c.get(previousDay), c.pct)}</div>
            <div>前週比 {diffText(value, weekAgo && c.get(weekAgo), c.pct)}</div>
          </div>
          <div className='text-fg-muted text-[11px] mt-xs'>{c.hint}</div>
        </Card>
      );
    })}
  </div>
);

// ---- ユーザー区分 -----------------------------------------------------------

const round1 = (v: number | undefined) =>
  typeof v === 'number' ? Math.round(v * 10) / 10 : undefined;

/**
 * ユーザー全体を「複数人家族に所属 / 未所属・招待発行済み / 未所属・招待未発行」に分けた構成。
 * 値は集計を実行した時点（executedAt）のもので、対象日（targetDate）の値ではない。
 * 家族化がどこまで進んでいるかを最新値で見せる（3区分なので系列色の先頭3色に収まる）。
 */
const UserSegmentCard: React.FC<{ latest: IKpiSnapshot }> = ({ latest }) => {
  const s = latest.snapshot;
  const segments = [
    { key: 'multi', label: '複数人家族に所属', value: s.usersInMultiMemberFamily },
    // 下2つは「複数人家族に所属していない」人（1人家族のほか、家族に未所属の人も含む）
    {
      key: 'invited',
      label: '未所属・招待リンク発行済み',
      value: s.usersNotInMultiWithInviteIssued,
    },
    {
      key: 'notInvited',
      label: '未所属・招待リンク未発行',
      value: s.usersNotInMultiWithoutInvite,
    },
  ].map((seg, i) => ({ ...seg, color: SERIES_COLORS[i] }));

  const total =
    typeof s.usersInMultiMemberFamily === 'number' &&
    typeof s.usersNotInMultiMemberFamily === 'number'
      ? s.usersInMultiMemberFamily + s.usersNotInMultiMemberFamily
      : null;
  const share = (v: number | undefined) =>
    total && typeof v === 'number' ? Math.round((v / total) * 1000) / 10 : null;

  return (
    <Card
      size='small'
      title={
        <span>
          ユーザー区分
          <Text type='secondary' className='text-[12px] font-normal ml-sm'>
            {moment(latest.executedAt).utcOffset(9 * 60).format('YYYY/MM/DD HH:mm')} 時点
            {total !== null && `・${formatNumber(total)}人`}
          </Text>
        </span>
      }
    >
      <div className='grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-xl'>
        <div className='flex flex-col gap-md'>
          {total && segments.every((seg) => typeof seg.value === 'number') ? (
            // 区分が1つでも欠けていると残りが横に伸びて割合を誤って見せるので、揃ったときだけ描く
            <div className='flex h-[16px] gap-[2px]' role='img' aria-label='ユーザー区分の構成'>
              {segments.map((seg) =>
                seg.value ? (
                  <AntTooltip
                    key={seg.key}
                    title={`${seg.label} ${formatNumber(seg.value)}人（${formatNumber(
                      share(seg.value),
                      '%',
                    )}）`}
                  >
                    <div
                      className='h-full first:rounded-l-[4px] last:rounded-r-[4px] min-w-[3px]'
                      style={{ flexGrow: seg.value, flexBasis: 0, background: seg.color }}
                    />
                  </AntTooltip>
                ) : null,
              )}
            </div>
          ) : (
            <Text type='secondary'>—</Text>
          )}
          <table className='text-[13px]'>
            <tbody>
              {segments.map((seg) => (
                <tr key={seg.key}>
                  <td className='py-[2px]'>
                    <span
                      className='inline-block w-[8px] h-[8px] rounded-full mr-sm'
                      style={{ background: seg.color }}
                    />
                    <span className='text-fg'>{seg.label}</span>
                  </td>
                  <td className='py-[2px] text-right text-fg font-semibold'>
                    {formatNumber(seg.value, '人')}
                  </td>
                  <td className='py-[2px] pl-md text-right text-fg-muted w-[64px]'>
                    {formatNumber(share(seg.value), '%')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className='flex flex-col gap-md text-[13px]'>
          <div>
            <div className='text-fg-muted text-[12px]'>アクティブな複数人家族</div>
            <div className='text-fg text-[20px] font-semibold leading-tight'>
              {formatNumber(s.activeMultiMemberFamilies, '家族')}
            </div>
            <div className='text-fg-muted text-[12px]'>
              平均 {formatNumber(round1(s.avgMembers), '人')} / 中央値{' '}
              {formatNumber(round1(s.medianMembers), '人')} / 最大 {formatNumber(s.maxMembers, '人')}
            </div>
          </div>
          <div>
            <div className='text-fg-muted text-[12px]'>AIエージェント配信対象</div>
            <div className='text-fg text-[20px] font-semibold leading-tight'>
              {formatNumber(s.agentTargetFamilies, '家族')}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

// ---- 表 -----------------------------------------------------------------

/** 有効期限が基準日（集計を実行した日、JST）より前に切れているコード */
const isExpired = (r: IKpiCompanyCode, asOfDate: string): boolean =>
  !!r.validTo && moment(r.validTo).format('YYYY-MM-DD') < asOfDate;

/**
 * 団体コード別の利用状況。一度も使われていないコードは数が多い（stg で 220 件中 166 件）ので
 * 行には出さず、件数だけ添える。
 * 「紐付け中」は解除されていない紐付けの数で、有効期限が切れていても数える（旧 kpi-collector 3-5 と同じ）。
 */
const CompanyCodeTable: React.FC<{ rows: IKpiCompanyCode[]; asOfDate: string }> = ({
  rows: allRows,
  asOfDate,
}) => {
  const rows = allRows.filter((r) => r.totalActivations > 0);
  const unusedCount = allRows.length - rows.length;

  return (
    <Card
      size='small'
      title='団体コード別の利用状況（最新）'
      extra={
        unusedCount > 0 && (
          <Text type='secondary' className='text-[12px]'>
            ほか {formatNumber(unusedCount)} 件は利用なし
          </Text>
        )
      }
    >
      <Table
        size='small'
        rowKey={(r) => `${r.companyName}-${r.code}`}
        pagination={false}
        scroll={{ x: 640, y: 320 }}
        dataSource={rows}
        locale={{ emptyText: '団体コードの利用はありません' }}
        summary={() =>
          rows.length > 1 ? (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  合計
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align='right'>
                  {formatNumber(rows.reduce((a, r) => a + r.activeFamilies, 0))}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align='right'>
                  {formatNumber(rows.reduce((a, r) => a + r.totalActivations, 0))}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align='right'>
                  {formatNumber(rows.reduce((a, r) => a + r.removedCount, 0))}
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          ) : null
        }
        columns={[
          { title: '団体', dataIndex: 'companyName', key: 'companyName' },
          { title: 'コード', dataIndex: 'code', key: 'code' },
          {
            title: '有効期間',
            key: 'valid',
            render: (_: unknown, r: IKpiCompanyCode) => (
              <Space size={4} wrap>
                <span>
                  {`${r.validFrom ? moment(r.validFrom).format('YYYY/MM/DD') : '—'} 〜 ${
                    r.validTo ? moment(r.validTo).format('YYYY/MM/DD') : '無期限'
                  }`}
                </span>
                {isExpired(r, asOfDate) && <Tag>期限切れ</Tag>}
              </Space>
            ),
          },
          {
            title: '紐付け中の家族',
            dataIndex: 'activeFamilies',
            key: 'activeFamilies',
            align: 'right',
            render: (v: number, r: IKpiCompanyCode) =>
              r.quantity ? `${formatNumber(v)} / ${formatNumber(r.quantity)}` : formatNumber(v),
          },
          {
            title: '累計利用',
            dataIndex: 'totalActivations',
            key: 'totalActivations',
            align: 'right',
            render: (v: number) => formatNumber(v),
          },
          {
            title: '解除',
            dataIndex: 'removedCount',
            key: 'removedCount',
            align: 'right',
            render: (v: number) => formatNumber(v),
          },
        ]}
      />
    </Card>
  );
};

/** 期間内のゲーム種別ごとの購入（一覧 API の各行の内訳を合計） */
const GamePurchaseTable: React.FC<{ rows: IKpiSnapshot[] }> = ({ rows }) => {
  const totals = useMemo(() => {
    const byType: Record<string, { gameType: string; purchases: number; revenue: number }> = {};
    rows.forEach((r) =>
      (r.breakdowns.gamePurchases ?? []).forEach((p) => {
        const t = (byType[p.gameType] ??= { gameType: p.gameType, purchases: 0, revenue: 0 });
        t.purchases += p.purchases;
        t.revenue += p.revenue;
      }),
    );

    return Object.values(byType).sort((a, b) => b.revenue - a.revenue);
  }, [rows]);

  return (
    <Card size='small' title='ゲーム種別ごとの購入（表示期間の合計）'>
      <Table
        size='small'
        rowKey='gameType'
        pagination={false}
        dataSource={totals}
        locale={{ emptyText: '期間内の購入はありません' }}
        columns={[
          { title: 'ゲーム', dataIndex: 'gameType', key: 'gameType' },
          {
            title: '購入数',
            dataIndex: 'purchases',
            key: 'purchases',
            align: 'right',
            render: (v: number) => formatNumber(v),
          },
          {
            title: '金額',
            dataIndex: 'revenue',
            key: 'revenue',
            align: 'right',
            render: (v: number) => formatNumber(v, '円'),
          },
        ]}
      />
    </Card>
  );
};

// ---- 画面 ---------------------------------------------------------------

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className='flex flex-col gap-md'>
    <Title level={5} className='!mb-0'>
      {title}
    </Title>
    <div className='grid grid-cols-1 lg:grid-cols-2 gap-md'>{children}</div>
  </section>
);

export const Dashboard: React.FC<IProps> = ({ title }) => {
  const [range, setRange] = useState<Range>('90');
  const [granularity, setGranularity] = useState<Granularity>('day');

  const latestQuery = useQuery(['kpi-latest'], () => KpiService.latest());
  const latest = latestQuery.data?.data;
  const latestDate = latest?.latest?.targetDate;

  const from =
    range === 'all' || !latestDate
      ? ALL_FROM
      : moment(latestDate, 'YYYY-MM-DD')
          .subtract(Number(range) - 1, 'days')
          .format('YYYY-MM-DD');

  const listQuery = useQuery(
    ['kpi-list', from, latestDate],
    () => KpiService.list({ from, to: latestDate }),
    { enabled: !!latestDate },
  );
  const rows = useMemo(() => listQuery.data?.data ?? [], [listQuery.data]);

  const buckets = useMemo(() => toBuckets(rows, METRICS, granularity), [rows, granularity]);
  const missing = useMemo(() => findMissingDates(rows), [rows]);

  const note = (id: MetricId, extra?: string) => joinNotes(aggNote(granularity, M[id].agg), extra);

  if (latestQuery.isError) {
    return <LoadError onRetry={() => latestQuery.refetch()} />;
  }

  return (
    <ConfigProvider theme={SURFACE_THEME}>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <div className='flex flex-col gap-xl pb-xl'>
        <div className='flex flex-wrap items-end justify-between gap-md'>
          <div>
            <Title level={4} className='!mb-xs'>
              KPI ダッシュボード
            </Title>
            {latest?.latest && (
              <Text type='secondary' className='text-[12px]'>
                最新 {moment(latest.latest.targetDate).format('YYYY/MM/DD')} 分 ・ 最終更新{' '}
                {moment(latest.latest.executedAt).format('YYYY/MM/DD HH:mm')}（毎朝 7:00 に前日分を集計）
              </Text>
            )}
          </div>
          {latest?.latest && (
            <Space wrap>
              <Segmented
                options={RANGE_OPTIONS}
                value={range}
                onChange={(v) => setRange(v as Range)}
              />
              <Segmented
                options={GRANULARITY_OPTIONS}
                value={granularity}
                onChange={(v) => setGranularity(v as Granularity)}
              />
            </Space>
          )}
        </div>

        {latestQuery.isLoading && (
          <div className='flex justify-center py-3xl'>
            <Spin />
          </div>
        )}

        {latest && !latest.latest && (
          <Card>
            <Empty description='まだ集計がありません（毎朝 7:00 に前日分を集計します）' />
          </Card>
        )}

        {latest?.latest && (
          <>
            <KpiCards
              latest={latest.latest}
              previousDay={latest.previousDay}
              weekAgo={latest.weekAgo}
            />

            <UserSegmentCard latest={latest.latest} />

            <DemographicsCard latest={latest.latest} />

            {latest.latest.targetDate < expectedLatestDate() && (
              <Alert
                type='warning'
                showIcon
                message={`${moment(expectedLatestDate()).format('YYYY/MM/DD')} 分の集計がまだありません`}
                description={
                  <Text className='text-[12px]'>
                    毎朝の集計が失敗した可能性があります。表示中の数字は{' '}
                    {moment(latest.latest.targetDate).format('YYYY/MM/DD')} 分までです。
                  </Text>
                }
              />
            )}

            {listQuery.isError && <LoadError onRetry={() => listQuery.refetch()} />}

            {missing.length > 0 && (
              <Alert
                type='warning'
                showIcon
                message={`集計が無い日が ${missing.length} 日あります`}
                description={
                  <Text className='text-[12px]'>
                    {missing.slice(0, 10).join(', ')}
                    {missing.length > 10 ? ' ほか' : ''}
                    。グラフではその日が抜けます（週次・月次では残りの日から計算）。
                  </Text>
                }
              />
            )}

            {listQuery.isLoading && (
              <div className='flex justify-center py-3xl'>
                <Spin />
              </div>
            )}

            {/* 取得に失敗したときは期間の合計や「購入なし」を出さない（0件に見えるため） */}
            {listQuery.isSuccess && (
              <>
                <Section title='家族化'>
                  <KpiChart
                    title='複数人家族数'
                    note={note(
                      'multiMemberFamilies',
                      'メンバー2人以上の家族。アクティブ＝直近30日にトークか投稿があった家族（集計した朝の時点）',
                    )}
                    kind='line'
                    buckets={buckets}
                    series={series('multiMemberFamilies', 'activeMultiMemberFamilies')}
                  />
                  <KpiChart
                    title='2人目が加わった家族・純増'
                    note={joinNotes(
                      granularity === 'day' ? undefined : '2人目は期間の合計、純増は期間の最終日どうしの差',
                      '2人目＝各家族で最初に2人目が加わった日に数える（1人に戻った後の再加入は数えない）。純増＝複数人家族数の増減（脱退・削除で1人以下に戻った家族を引き、再び2人以上になった家族を足す）',
                    )}
                    kind='bar'
                    buckets={buckets}
                    series={series('secondMemberFamilies', 'multiMemberFamiliesNet')}
                  />
                  <KpiChart
                    title='複数人家族率'
                    note={note('multiMemberRatePct', '複数人家族 ÷ 総家族')}
                    kind='line'
                    buckets={buckets}
                    series={series('multiMemberRatePct')}
                    unit='%'
                  />
                  <KpiChart
                    title='招待リンク'
                    note={note(
                      'inviteLinksIssued',
                      '発行＝招待画面でリンクを作った回数（同じ人が何度も作ると重複して数える）。参加＝そのリンクで家族に入った人数',
                    )}
                    kind='bar'
                    buckets={buckets}
                    series={series('inviteLinksIssued', 'inviteLinksUsed')}
                  />
                  <KpiChart
                    title='招待した家族・人'
                    note={note(
                      'inviteIssuingFamilies',
                      '招待リンクを1回以上作った家族と人の数（同じ日の連打は1と数える）',
                    )}
                    kind='bar'
                    buckets={buckets}
                    series={series('inviteIssuingFamilies', 'inviteIssuingUsers')}
                  />
                  <FamilyCohortTable />
                </Section>

                <Section title='成長'>
                  <KpiChart
                    title='アクティブユーザー'
                    note={note('dau', 'その日の朝時点で直近24時間 / 7日 / 30日にアプリを開いた人')}
                    kind='line'
                    buckets={buckets}
                    series={series('dau', 'wau', 'mau')}
                  />
                  <KpiChart
                    title='アクティブ家族'
                    note={note('dag', '直近24時間 / 7日 / 30日にトークか投稿があった家族')}
                    kind='line'
                    buckets={buckets}
                    series={series('dag', 'wag', 'mag')}
                  />
                  <KpiChart
                    title='新規ユーザー・新規家族'
                    note={note('newUsers')}
                    kind='bar'
                    buckets={buckets}
                    series={series('newUsers', 'newFamilies')}
                  />
                  <KpiChart
                    title='家族数'
                    note={note('totalFamilies', '複数人家族＝メンバー2人以上')}
                    kind='line'
                    buckets={buckets}
                    series={series('totalFamilies', 'multiMemberFamilies')}
                  />
                </Section>

                <Section title='エンゲージメント'>
                  <KpiChart
                    title='投稿・コメント・いいね'
                    note={note('posts', 'いいね＝その日に初めて押されたいいね')}
                    kind='bar'
                    buckets={buckets}
                    series={series('posts', 'comments', 'favorites')}
                  />
                  <KpiChart
                    title='家族トーク'
                    note={note(
                      'familyTalkComments',
                      '家族タブのトークで利用者が送った分（フクマルの投稿は含まない）。コメント＝テキスト・画像・動画。リアクション＝その日に付けられ、今も残っているもの。2026/10/01 以降のみ',
                    )}
                    kind='bar'
                    buckets={buckets}
                    series={series('familyTalkComments', 'familyTalkStamps', 'familyTalkReactions')}
                  />
                  <KpiChart
                    title='家族トークのその他'
                    note={note(
                      'familyTalkOthers',
                      '家族トークに送られた共有カード（かぞくごと・脳トレイラストなど）。2026/10/01 以降のみ',
                    )}
                    kind='bar'
                    buckets={buckets}
                    series={series('familyTalkOthers')}
                  />
                  <KpiChart
                    title='トーク（家族・フクマル）'
                    note={note(
                      'messages',
                      '利用者が送ったメッセージ（スタンプを含み、リアクションは含まない）。家族トーク＝コメント＋スタンプ＋その他、フクマル＝フクマルとの個人チャット（この2つは 2026/10/01 以降のみ）。トーク合計は両方を含む従来の値で、古いアンケートのトークも入るため2つの和より少し多いことがある',
                    )}
                    kind='bar'
                    buckets={buckets}
                    series={series('messages', 'familyTalkSent', 'fukumaruTalkMessages')}
                  />
                  <KpiChart
                    title='その日に動いた人・家族'
                    note={note(
                      'stepActiveUsers',
                      '歩数を送ったユーザー数と、トークか投稿があった家族数（暦日）',
                    )}
                    kind='line'
                    buckets={buckets}
                    series={series('stepActiveUsers', 'activeFamilies')}
                  />
                  <KpiChart
                    title='脳トレの利用者数'
                    note={note('cogevoUu', 'その日に1回以上プレイしたユーザー数')}
                    kind='line'
                    buckets={buckets}
                    series={series('cogevoUu', 'nouknowUu')}
                  />
                </Section>

                <Section title='収益'>
                  <KpiChart
                    title='新規課金家族'
                    note={note('newPayingFamilies')}
                    kind='bar'
                    buckets={buckets}
                    series={series('newPayingFamilies')}
                  />
                  <KpiChart
                    title='新規課金額'
                    note={note('newPayingRevenue')}
                    kind='bar'
                    buckets={buckets}
                    series={series('newPayingRevenue')}
                    unit='円'
                  />
                  <KpiChart
                    title='課金率'
                    note={note('payingRatePct', 'MAG のうち課金中の家族の割合')}
                    kind='line'
                    buckets={buckets}
                    series={series('payingRatePct')}
                    unit='%'
                  />
                  <GamePurchaseTable rows={rows} />
                </Section>

                <section className='flex flex-col gap-md'>
                  <CompanyCodeTable
                    rows={latest.latest.breakdowns.companyCodes ?? []}
                    // 利用数は集計を実行した時点の値なので、期限切れも同じ時点で判定する
                    asOfDate={moment(latest.latest.executedAt)
                      .utcOffset(9 * 60)
                      .format('YYYY-MM-DD')}
                  />
                </section>

                <Section title='リテンション・健全性'>
                  <KpiChart
                    title='7日継続率・プッシュ到達可能率'
                    note={note(
                      'retention7dPct',
                      '継続率＝登録7日以上のうち直近7日にオンライン。到達可能率＝アクティブのうち通知トークンあり',
                    )}
                    kind='line'
                    buckets={buckets}
                    series={series('retention7dPct', 'pushReachablePct')}
                    unit='%'
                  />
                  <KpiChart
                    title='離脱メンバー・問い合わせ'
                    note={note('leavedMembers')}
                    kind='bar'
                    buckets={buckets}
                    series={series('leavedMembers', 'inquiries')}
                  />
                </Section>

              </>
            )}
          </>
        )}
      </div>
    </ConfigProvider>
  );
};
